import { cloneDeep } from "lodash";
import { v4 as uuid } from "uuid";
import { ActionContext, Module } from "vuex";

import AdvertisersApi from "@/api/AdvertisersApi";
import AgenciesApi from "@/api/AgenciesApi";
import { getProjectsApi } from "@/api/ProjectsApi";
import { normalizeAdvertiserShape } from "@/helpers/unpacking";
import { RootState } from "@/store";
import { AdvertiserListEntity, AgencyListEntity } from "@/types/plans";
import { BusinessGoal, FullAdvertiserProfile, Plan, Project, Scenario } from "@/types/project";

export interface AdvertiserB2 {
  pixeling_experience?: number | string;
  website_conversion_experience?: number | string;
  average_order_value?: number | string;
  brand_recognition?: number | string;
  estimated_time_to_conversion?: number | string;
  estimated_audience_size?: number | string;
}

export interface Advertiser {
  id: string;
  name: string;
  industry?: string;
  sub_industry?: string;
  url?: string;
  b2b?: AdvertiserB2 | null;
  b2c?: AdvertiserB2 | null;
  agency_id?: string;
}

export interface ProjectEditState {
  selectedAdvertiser: AdvertiserListEntity | null;
  selectedAdvertiserDetailed: Advertiser | null;
  advertisers: Advertiser[];
  selectedAgency: AgencyListEntity | null;
  loading: boolean;
  agencies: AgencyListEntity[];
  initialProject: Project;
  localProject: Project;
  localAdvertiser: FullAdvertiserProfile;
  deletedScenarios: string[];
}

const state: ProjectEditState = {
  selectedAdvertiser: null,
  selectedAdvertiserDetailed: null,
  loading: false,
  selectedAgency: null,
  advertisers: [],
  agencies: [],
  initialProject: {} as Project,
  localProject: {} as Project,
  localAdvertiser: {} as FullAdvertiserProfile,
  deletedScenarios: [],
};

const projectEdit: Module<ProjectEditState, RootState> = {
  namespaced: true,

  state,

  mutations: {
    setSelectedAdvertiser(state, advertiser: AdvertiserListEntity | null): void {
      state.selectedAdvertiser = advertiser;
    },
    setSelectedAdvertiserDetailed(state, advertiser: Advertiser | null): void {
      state.selectedAdvertiserDetailed = advertiser;
    },
    setLoading(state, loading: boolean): void {
      state.loading = loading;
    },
    setLocalProject(state, project: Project): void {
      state.localProject = project;
    },
    setInitialProject(state, project: Project): void {
      state.initialProject = project;
    },
    setLocalAdvertiser(state, advertiser: FullAdvertiserProfile): void {
      state.localAdvertiser = advertiser;
    },
    setAdvertisersList(state, data: AdvertiserListEntity[]) {
      state.advertisers = data;
    },
    setSelectedAgency(state, agency: AgencyListEntity) {
      state.selectedAgency = agency;
    },
    setAgenciesList(state, agencies: AgencyListEntity[]): void {
      state.agencies = agencies;
    },
    setDeletedScenarios(state, scenarios: string[]): void {
      state.deletedScenarios = scenarios;
    },
  },

  actions: {
    async duplicateBusinessGoal({ state, commit, rootGetters }, businessgoalId: string) {
      const project = cloneDeep(state.localProject);
      if (!project?.business_goals?.length) return;

      const src = project.business_goals.find((bg: BusinessGoal) => bg?.id === businessgoalId);
      if (!src) return;

      const user = rootGetters?.["auth/user"] ?? {};
      const ownerName = user?.attributes?.name ?? user?.name ?? "";
      const ownerEmail = user?.attributes?.email ?? user?.email ?? "";

      const duplicated = cloneDeep(src) as BusinessGoal;
      duplicated.id = uuid();
      duplicated.name = `${src.name} - Copy`;
      duplicated.owned_by = ownerName;
      duplicated.owner = ownerEmail;

      duplicated.plans = (src.plans ?? []).map((plan: Plan) => {
        const p = cloneDeep(plan) as Plan;
        p.id = uuid();
        p.owned_by = ownerName;
        p.owner = ownerEmail;
        p.approved = false;
        p.scenarios = (plan.scenarios ?? []).map((sc: Scenario) => {
          const s = cloneDeep(sc) as Scenario;
          s.id = uuid();
          s.owned_by = ownerName;
          s.owner = ownerEmail;
          s.approved = false;
          s.output = cloneDeep(sc.output);
          return s;
        });
        return p;
      });

      project.business_goals.push(duplicated);

      commit("setLocalProject", project);
    },
    async deleteBusinessGoal({ state, commit }, businessgoalId: string) {
      const project = cloneDeep(state.localProject);
      const list = project?.business_goals ?? [];
      const idx = list.findIndex((bg: BusinessGoal) => bg?.id === businessgoalId);
      if (idx < 0) return false;
      const bg = list[idx];
      const hasApprovals = (bg?.plans ?? []).some(
        (p: Plan) => p?.approved || (p?.scenarios ?? []).some((sc: Scenario) => sc?.approved)
      );
      if (hasApprovals) return false;
      project.business_goals = list.filter((b: BusinessGoal) => b?.id !== businessgoalId);
      commit("setLocalProject", project);
      return true;
    },
    async fetchProjectById(ctx: ActionContext<ProjectEditState, RootState>, projectId: string) {
      ctx.commit("setLoading", true);
      try {
        const project = await getProjectsApi().getProject(projectId);
        ctx.commit("setLocalProject", cloneDeep(project));
        ctx.commit("setInitialProject", cloneDeep(project));
        return project;
      } catch (error) {
        ctx.commit("setLocalProject", null as unknown as Project);
        ctx.commit("setInitialProject", null as unknown as Project);
        throw error;
      } finally {
        ctx.commit("setLoading", false);
      }
    },

    async duplicatePlan({ state, commit, rootGetters }, planId: string) {
      const project = cloneDeep(state.localProject);
      if (!project?.business_goals?.length || !planId) return;

      let bgIdx = -1;
      let srcPlan: Plan | null = null;

      for (let i = 0; i < project.business_goals.length; i++) {
        const bg = project.business_goals[i];
        const found = (bg?.plans || []).find((p: Plan) => p?.id === planId) as Plan | undefined;
        if (found) {
          bgIdx = i;
          srcPlan = found || null;
          break;
        }
      }
      if (!srcPlan || bgIdx === -1) return;

      const user = rootGetters?.["auth/user"] ?? {};
      const ownerName = user?.attributes?.name ?? user?.name ?? "";
      const ownerEmail = user?.attributes?.email ?? user?.email ?? "";

      const duplicated: Plan = cloneDeep(srcPlan);
      duplicated.id = uuid();
      duplicated.name = `${srcPlan.name} - Copy`;
      duplicated.owned_by = ownerName;
      duplicated.owner = ownerEmail;
      duplicated.approved = false;

      duplicated.scenarios = (srcPlan.scenarios ?? []).map((sc: Scenario) => {
        const s: Scenario = cloneDeep(sc);
        s.id = uuid();
        s.owned_by = ownerName;
        s.owner = ownerEmail;
        s.approved = false;
        s.output = cloneDeep(sc.output);
        return s;
      });

      const plans = project.business_goals[bgIdx].plans ?? [];
      project.business_goals[bgIdx].plans = [...plans, duplicated];

      commit("setLocalProject", project);
    },
    async deletePlan({ state, commit }, planId: string): Promise<boolean> {
      const project = cloneDeep(state.localProject);
      if (!project?.business_goals?.length || !planId) return false;

      let bgIdx = -1;
      let planIdx = -1;
      let targetPlan: Plan | null = null;

      for (let i = 0; i < project.business_goals.length; i++) {
        const plans = project.business_goals[i]?.plans ?? [];
        const idx = plans.findIndex((p: Plan) => p?.id === planId);
        if (idx !== -1) {
          bgIdx = i;
          planIdx = idx;
          targetPlan = plans[idx] as Plan;
          break;
        }
      }
      if (bgIdx === -1 || planIdx === -1 || !targetPlan) return false;

      const canDelete = !targetPlan.approved && (targetPlan.scenarios ?? []).every((s: Scenario) => !s?.approved);

      if (!canDelete) return false;

      const nextPlans = (project.business_goals[bgIdx].plans ?? []).filter((p: Plan) => p?.id !== planId);
      project.business_goals[bgIdx].plans = nextPlans;

      commit("setLocalProject", project);
      return true;
    },
    async deleteScenario({ state, commit }, scenarioId: string): Promise<boolean> {
      const project = cloneDeep(state.localProject);
      if (!project?.business_goals?.length) return false;

      for (const bg of project.business_goals) {
        const plans = bg?.plans ?? [];
        for (const plan of plans) {
          const scenarios = plan?.scenarios ?? [];
          const idx = scenarios.findIndex((sc) => sc?.id === scenarioId);

          if (idx !== -1) {
            const scenario = scenarios[idx];

            if (scenario?.approved) return false;

            plan.scenarios = scenarios.filter((sc) => sc.id !== scenarioId);

            commit("setLocalProject", project);
            return true;
          }
        }
      }

      return false;
    },
    async syncApprovalStatus(ctx: ActionContext<ProjectEditState, RootState>, projectId: string) {
      try {
        const fresh = await getProjectsApi().getProject(projectId);

        ctx.commit("setInitialProject", cloneDeep(fresh));

        const nextLocal = cloneDeep(ctx.state.localProject || {});
        const freshProject = fresh || {};
        (nextLocal as Project).last_modified = (freshProject as Project)?.last_modified;

        const freshBGs = (freshProject as Project)?.business_goals || [];
        const localBGs = (nextLocal as Project)?.business_goals || [];

        const freshBGMap = new Map<string, BusinessGoal>();
        for (const bg of freshBGs) {
          if (bg?.id) freshBGMap.set(bg.id, bg);
        }

        for (const lbg of localBGs) {
          const fbg = lbg?.id ? freshBGMap.get(lbg.id) : null;
          const lPlans = (lbg?.plans || []) as Plan[];
          const fPlans = (fbg?.plans || []) as Plan[];

          const fPlanMap = new Map<string, Plan>();
          for (const p of fPlans) {
            if (p?.id) fPlanMap.set(p.id, p);
          }

          for (const lp of lPlans) {
            const fp = lp?.id ? fPlanMap.get(lp.id) : null;
            if (fp) {
              lp.approved = !!fp.approved;

              const lScs = (lp?.scenarios || []) as Scenario[];
              const fScs = (fp?.scenarios || []) as Scenario[];

              const fScMap = new Map<string, Scenario>();
              for (const sc of fScs) {
                if (sc?.id) fScMap.set(sc.id, sc);
              }

              for (const lsc of lScs) {
                const fsc = lsc?.id ? fScMap.get(lsc.id) : null;
                if (fsc) {
                  lsc.approved = !!fsc.approved;
                }
              }
            }
          }
        }

        ctx.commit("setLocalProject", nextLocal);
      } catch (e) {
        console.error("syncApprovalStatus failed", e);
      }
    },

    async fetchAgencies({ commit }) {
      commit("setLoading", true);
      try {
        const api = new AgenciesApi();
        const response = await api.getAllAgencies();
        commit("setAgenciesList", response);
      } catch (e) {
        console.error(e);
      } finally {
        commit("setLoading", false);
      }
    },

    setSelectedAdvertiser({ commit }, advertiser: AdvertiserListEntity | null): void {
      commit("setSelectedAdvertiser", advertiser);
    },

    setSelectedAdvertiserDetailed({ commit }, advertiser: Advertiser | null): void {
      commit("setSelectedAdvertiserDetailed", advertiser);
    },

    async fetchAdvertisersByAgency({ commit }, agencyName: string) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const api = new AdvertisersApi();
        const response = await api.getAdvertisersByAgency(agencyName);
        commit("setAdvertisersList", response);
      } catch (e) {
        commit("setError", "Failed to advertisers list");
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchAdvertisersData({ commit, state }) {
      const advertiser = state.selectedAdvertiser;
      if (!advertiser?.id) {
        console.error("No advertiser found");
        return;
      }

      commit("setLoading", true);
      try {
        const api = new AdvertisersApi();
        const response = await api.getAllAdvertiserByID(advertiser.id);
        commit("setSelectedAdvertiserDetailed", response);
        commit("setLocalAdvertiser", response);
      } catch (e) {
        console.error(e);
      } finally {
        commit("setLoading", false);
      }
    },
    async updateAdvertiser({ commit, dispatch }, { id, advertiser }: { id: string; advertiser: Advertiser }) {
      commit("setLoading", true);

      try {
        const clean = (obj: Advertiser): Advertiser =>
          Object.fromEntries(
            (Object.entries(obj) as [keyof Advertiser, Advertiser[keyof Advertiser]][])
              .filter(([, v]) => {
                if (v === null || v === "" || v === undefined) return false;
                if (typeof v === "object" && v !== null && !Array.isArray(v)) {
                  const cleaned = clean(v as unknown as Advertiser);
                  return Object.keys(cleaned).length > 0;
                }
                return true;
              })
              .map(
                ([k, v]) =>
                  [
                    k,
                    typeof v === "object" && v !== null && !Array.isArray(v)
                      ? (clean(v as unknown as Advertiser) as Advertiser[typeof k])
                      : (v as Advertiser[typeof k]),
                  ] as [keyof Advertiser, Advertiser[keyof Advertiser]]
              )
          ) as unknown as Advertiser;

        const cleanedAdvertiser = {
          ...clean(advertiser),
        };

        const api = new AdvertisersApi();
        const updated = await api.updateAdvertiserById(id, cleanedAdvertiser);
        commit("setSelectedAdvertiserDetailed", updated);
        await dispatch("fetchAdvertisers");
        return updated;
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        commit("setLoading", false);
      }
    },

    async fetchAdvertisers({ commit }) {
      commit("setLoading", true);
      try {
        const api = new AdvertisersApi();
        const response = await api.getAllAdvertisers();
        commit("setAdvertisersList", response);
      } catch (e) {
        console.error(e);
      } finally {
        commit("setLoading", false);
      }
    },

    initLocalAdvertiserFromProject({ commit }, project) {
      const a = project?.advertiser_profile || {};
      commit("setLocalAdvertiser", normalizeAdvertiserShape({ ...a, id: project?.advertiser_id }));
    },
  },

  getters: {
    selectedAdvertiser: (state): AdvertiserListEntity | null => state.selectedAdvertiser,
    selectedAdvertiserDetailed: (state): Advertiser | null => state.selectedAdvertiserDetailed,
    loading: (state): boolean => state.loading,
    advertisers: (state): AdvertiserListEntity[] => state.advertisers,
    agencies: (state): AgencyListEntity[] => state.agencies,
    selectedAgency: (state): AgencyListEntity | null => state.selectedAgency,
    initialProject: (state): Project => state.initialProject,
    localProject: (state): Project => state.localProject,
    localAdvertiser: (state): FullAdvertiserProfile => state.localAdvertiser,
    deletedScenarios: (state): string[] => state.deletedScenarios,
  },
};

export default projectEdit;
